import { useCallback, useContext } from 'react'

import { useTranslation } from 'react-i18next'

import { ResponseContext } from '@cash-web/protos/squareup/franklin/ResponseContextProto.pb'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { HttpError } from '@cash-web/shared-util-fetch'

export type GenericBlockerUpdate = {
  response_context?: ResponseContext
  status?: string
}

type ErrorStatus<T extends GenericBlockerUpdate> = Exclude<
  NonNullable<T['status']>,
  'SUCCESS' | 'SUCCESS_WITH_VERIFICATION_DATA'
>

export type ErrorMessages<T extends GenericBlockerUpdate> = {
  [key in ErrorStatus<T>]: string
}
export const useBlockerResponseHandler = () => {
  const { t } = useTranslation()
  const { setError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const genericError = t('Oops, something went wrong. Please refresh the page and try again.', {
    context: 'Generic response to user when we do not know how to handle a situtation that has occurred.',
  })

  const handleBlockerResponse = useCallback(
    async <T extends GenericBlockerUpdate>({
      trackingFlowName,
      promisedBlockerUpdate,
      defaultError,
      errors,
      isResponseSuccess,
      handleResponseStatusFromBlockerFlow,
      onFailure,
    }: {
      trackingFlowName?: string
      promisedBlockerUpdate: Promise<T>
      defaultError?: string
      errors?: ErrorMessages<T>
      isResponseSuccess?: (response: T) => boolean
      handleResponseStatusFromBlockerFlow?: (status: string) => void
      onFailure?: (response?: T) => void
    }) => {
      const handleError = async <T extends GenericBlockerUpdate>(
        error: GenericBlockerUpdate,
        errorMessages: ErrorMessages<T> | undefined,
        fallbackError: string
      ) => {
        if (error?.response_context?.scenario_plan?.blocker_descriptors != null) {
          await flow.updateWithResponse({
            response: Promise.resolve(error),
            queriesToInvalidate: [],
          })
        }
        if (error?.response_context?.dialog_message != null) {
          setError(error.response_context?.dialog_message)
        } else if (error?.response_context?.failure_message != null) {
          setError(error.response_context?.failure_message)
        } else {
          setError(errorMessages?.[error.status as ErrorStatus<T>] ?? fallbackError)
        }
      }
      const blockerUpdate = promisedBlockerUpdate

      const fallbackError = defaultError || genericError
      try {
        const response = await blockerUpdate
        if (isResponseSuccess?.(response) || response.status === 'SUCCESS') {
          if (flow.isInProgress) {
            await flow.updateWithResponse({
              response: Promise.resolve(response),
              queriesToInvalidate: [],
            })
          } else {
            await flow.startWithResponse({
              trackingFlowName: trackingFlowName ?? 'blocker-response-handler',
              response: Promise.resolve(response),
              queriesToInvalidate: [],
            })
          }
        } else {
          onFailure?.(response)
          await handleError(response, errors, fallbackError)
        }
        return response
      } catch (e) {
        const error = e as HttpError<T>
        const response = error.response?.body

        if (typeof response !== 'string' && response != null) {
          response.status && handleResponseStatusFromBlockerFlow?.(response.status)

          onFailure?.(response)
          await handleError(response, errors, fallbackError)
          return response
        } else {
          onFailure?.()
          setError(fallbackError)
        }
      }
      return undefined
    },
    [genericError, flow, setError]
  )

  return { handleBlockerResponse, genericError }
}
